import { Injectable, inject, signal, EventEmitter } from '@angular/core';
import { Router } from '@angular/router';
import { firstValueFrom } from 'rxjs';

import { AuthApiService } from '../../../core/services/auth-api.service';
import { SessionService } from '../../../core/services/session.service';
import { AdminService } from '../../../core/services/admin.service';
import { HistoryService } from '../../rma/services/history.service';
import { LoginCredentials, AuthResult } from '../../../core/models/auth.model';
import { PubSubService } from '../../../core/services/pubsub.service';
import { SharedSource } from '../../../core/models/pubsub.model';
import { environment } from '../../../../environments/environment';
import { jwtDecode } from 'jwt-decode';
import { CookieService } from 'ngx-cookie-service';



@Injectable({
    providedIn: 'root'
})
export class AuthenticationService {
    private readonly authApiService = inject(AuthApiService);
    private readonly sessionService = inject(SessionService);
    private readonly router = inject(Router);
    private readonly pubSubService = inject(PubSubService);
    private cookieService = inject(CookieService);
    private readonly adminService = inject(AdminService);
    private readonly historyService = inject(HistoryService);

    // Signals
    xPath = signal(0);
    loginSuccess = signal(false);
    loginFail = signal(false);
    loading = signal(false);

    // Properties
    redirectUrl: string = '';
    userRole: string = '';
    roleChanged = new EventEmitter<string>();
    //Constants 
    private readonly ACCESS_TOKEN_COOKIE_KEY = `${environment.name}_rma_access_token`;
    private readonly EXPIRES_AT_COOKIE_KEY = `${environment.name}_rma_expires_at`;
    private readonly AUTH_REALM = environment.hubx_auth_realm;


    constructor() {
        // Session restoration is now handled by restoreSession() via APP_INITIALIZER
    }

    async initializeApp(): Promise<void> {
        // Delay initialization until after prerendering to avoid running in a discarded context.
        if ((document as any).prerendering) {
            await new Promise<void>(resolve =>
                document.addEventListener('prerenderingchange', () => resolve(), { once: true })
            );
        }
        await this.restoreSession();
    }

    async restoreSession(): Promise<void> {
        // Re-sync admin signals from storage — constructor ran during prerendering
        // against a discarded context, so signals may be stale after activation.
        this.adminService.syncFromStorage();

        const cookieKey = this.ACCESS_TOKEN_COOKIE_KEY;
        const expiresAtKey = this.EXPIRES_AT_COOKIE_KEY;
        const cookieExists = this.cookieService.check(cookieKey);

        if (cookieExists && !sessionStorage.getItem('access_token')) {
            const expiresAtStr = this.cookieService.get(expiresAtKey);
            if (expiresAtStr) {
                const expiresAt = +expiresAtStr;
                if (new Date().getTime() > expiresAt) {
                    this.deleteCookies();
                    return;
                }
            }

            const authResult: AuthResult = {
                accessToken: this.cookieService.get(cookieKey),
                expiresIn: +expiresAtStr,
                tokenType: 'Bearer',
                idToken: null,
                refreshToken: null
            };

            await this.setSession(authResult);
        } else if (sessionStorage.getItem('access_token')) {
            // Session already in storage (e.g. hash login wrote it before reload).
            // setSession won't run so we must ensure the impersonation session exists
            // for admin users before setupAdminImpersonationForUrl needs it.
            const userRole = sessionStorage.getItem('user_role');
            if (userRole?.toLowerCase() === 'admin' && !sessionStorage.getItem('impersonation_session_id')) {
                await firstValueFrom(this.adminService.createImpersonationSession());
            }
        }

        // For admin users on direct URL navigation to a claim, set up impersonation
        await this.setupAdminImpersonationForUrl(window.location.pathname);
    }

    /**
     * For admin users navigating directly to a claim URL, look up the claim's
     * company code via HistoryService and set impersonation to that company.
     * This replicates the forceImpersonationAndNavigate pattern from dashboard/navigation.
     */
    private async setupAdminImpersonationForUrl(url: string): Promise<void> {
        const userRole = sessionStorage.getItem('user_role');
        if (userRole?.toLowerCase() !== 'admin') {
            return;
        }

        // Check if URL is a claim deep link: /rma/claim/{id}
        const claimMatch = url.match(/\/rma\/claim\/(\d+)/);
        if (!claimMatch) {
            return;
        }

        // If a specific company is already impersonated, don't override
        const storedCardCode = sessionStorage.getItem('impersonation_company_code');
        if (storedCardCode !== null && storedCardCode !== '') {
            return;
        }

        const claimId = parseInt(claimMatch[1], 10);
        const sessionId = sessionStorage.getItem('impersonation_session_id');
        if (!sessionId) {
            return;
        }

        try {
            // Look up the claim's company code via History endpoint
            const historyResponse = await firstValueFrom(
                this.historyService.getHistoryLogs(undefined, undefined, claimId, undefined, undefined, 1, 1)
            );
            const log = historyResponse.historyLogs?.[0];

            if (log?.companyCode) {
                await firstValueFrom(this.adminService.setImpersonation(sessionId, log.companyCode));

                if (log.companyName) {
                    const displayName = `${log.companyCode} - ${log.companyName}`;
                    sessionStorage.setItem('impersonation_company_name', displayName);
                }
            }
        } catch (error) {
            console.error('Error setting up impersonation for claim:', error);
        }
    }

    async login(credentials: LoginCredentials): Promise<void> {
        if (this.loading()) {
            return;
        }

        try {
            this.loading.set(true);
            this.loginSuccess.set(false);
            this.loginFail.set(false);

            this.sessionService.resetSessionVariables();
            this.deleteStorage();
            this.deleteCookies();

            const authResult = await firstValueFrom(
                this.authApiService.login(
                    credentials.email,
                    credentials.password,
                    this.AUTH_REALM
                )
            );

            await this.handleLoginSuccess(authResult);
        } catch (error) {
            this.handleLoginError(error);
            throw error;
        }
    }


    private async handleLoginSuccess(authResult: AuthResult): Promise<void> {
        if (authResult && authResult.accessToken) {
            this.loginFail.set(false);

            // Initialize session first (stores user data in sessionStorage)
            await this.initialize(authResult);

            // Then emit loginSuccess signal so components can read the stored data
            this.loginSuccess.set(true);
            this.loading.set(false);
        }
    }

    private handleLoginError(error: any): void {
        this.loginSuccess.set(false);
        this.loginFail.set(true);
        this.loading.set(false);
    }

    private async initialize(authResult: AuthResult): Promise<void> {
        try {
            await this.setSession(authResult);

            // Redirect to redirectUrl if it exists, otherwise to default route
            const targetUrl = this.redirectUrl || '';
            if (targetUrl && targetUrl !== '/login') {
                this.redirectUrl = '';
                // For admin users redirecting to a claim, set up company impersonation first
                await this.setupAdminImpersonationForUrl(targetUrl);
                await this.router.navigateByUrl(targetUrl);
            } else {
                await this.router.navigate(['']);
            }

        } catch (error) {
            console.error('Error initializing user:', error);
        }
    }

    async logout(): Promise<void> {
        this.sessionService.resetSessionVariables();

        this.deleteStorage();
        this.deleteCookies();

        this.loginSuccess.set(false);
        this.loginFail.set(false);
        this.loading.set(false);
        
        await this.router.navigate(['']);
    }

    isAuthenticated(): boolean {
        return this.sessionService.isLoggedIn() && !this.sessionService.isTokenExpired();
    }

    getAccessToken(): string | null {
        return this.sessionService.getAccessToken();
    }

    getUserRole(): string {
        return this.userRole || sessionStorage.getItem('user_role') || '';
    }

    async handleAuthentication(): Promise<void> {
        const hashStr = window.location.hash;
        if (!hashStr || hashStr.length === 0) {
            return;
        }

        this.loading.set(true);

        try {
            const authResult = await this.parseHash();

            if (authResult && authResult.accessToken && authResult.idToken) {
                await this.setSession(authResult);
                // Clear the hash before reloading so handleAuthentication doesn't re-trigger
                history.replaceState(null, '', window.location.pathname);
                window.location.reload();
                return;
            }

            this.loading.set(false);

        } catch (error) {
            console.error('Authentication error:', error);
            this.loading.set(false);

            await this.router.navigate(['']);
        }
    }

    private async parseHash(): Promise<AuthResult | null> {
        let hashStr = window.location.hash.replace(/^#?\/?/, '');

        if (!hashStr) {
            return null;
        }

        const params = new URLSearchParams(hashStr);

        if (params.has('error')) {
            const error = params.get('error');
            const errorDescription = params.get('error_description');
            throw new Error(`${error}: ${errorDescription}`);
        }

        const accessToken = params.get('access_token');
        const idToken = params.get('id_token');
        const expiresIn = params.get('expires_in');
        const tokenType = params.get('token_type');

        if (!accessToken && !idToken) {
            return null;
        }

        return {
            accessToken: accessToken || '',
            idToken: idToken || null,
            expiresIn: expiresIn ? parseInt(expiresIn, 10) : 0,
            tokenType: tokenType || 'Bearer',
            refreshToken: params.get('refresh_token') || null
        };
    }

    private async setSession(authResult: AuthResult): Promise<void> {
        try {
            const accessTokenDecoded: any = jwtDecode(authResult.accessToken);
            const appMetadata = JSON.parse(accessTokenDecoded['https://www.hubx.com/app_metadata']);
            const userInfo = JSON.parse(accessTokenDecoded['https://www.hubx.com/user_info']);
            const expiresAt = JSON.parse(accessTokenDecoded['exp']) * 1000;

            this.userRole = appMetadata.Role;

            sessionStorage.setItem('access_token', authResult.accessToken);
            sessionStorage.setItem('id_token', authResult.idToken || '');
            sessionStorage.setItem('expires_at', JSON.stringify(expiresAt));
            sessionStorage.setItem('user_id', appMetadata.UserId);
            sessionStorage.setItem('partner_id', appMetadata.PartnerId || '');
            sessionStorage.setItem('user_email', userInfo.email);
            sessionStorage.setItem('user_role', this.userRole);
            sessionStorage.setItem('show_phone_popup', '1');

            // Store CardCode for Customer users
            if (appMetadata.CardCode) {
                sessionStorage.setItem('user_card_code', appMetadata.CardCode);
            }

            // this.roleChanged.emit(this.userRole);

            await this.setCookies(authResult.accessToken, expiresAt);

            if (appMetadata.PartnerId) {
                this.pubSubService.emit({
                    name: SharedSource.partnerIdReady,
                    data: true
                });
            }

            this.pubSubService.emit({
                name: SharedSource.roleChanged,
                data: this.userRole
            });

            this.sessionService.setLoggedIn(true);

            // Create impersonation session here so it exists before setupAdminImpersonationForUrl
            // and before loginSuccess fires — covers all login paths (form, cookie, hash)
            if (appMetadata.Role?.toLowerCase() === 'admin' && !sessionStorage.getItem('impersonation_session_id')) {
                await firstValueFrom(this.adminService.createImpersonationSession());
            }

        } catch (error) {
            console.error('Error setting session:', error);
            throw error;
        }
    }

    setCookies(accessToken: string, expiresAt: number) {
        const expirationDate = new Date(expiresAt);
        const domain = location.hostname;
        const secure = true;

        if (!this.cookieService.check(this.ACCESS_TOKEN_COOKIE_KEY)) {
            this.cookieService.set(this.ACCESS_TOKEN_COOKIE_KEY, accessToken, expirationDate, '/', domain, secure);
            this.cookieService.set(
                this.EXPIRES_AT_COOKIE_KEY, JSON.stringify(expiresAt), expirationDate, '/', domain, secure);
        }
    }

    verifyCookies(): boolean {
        const env = environment.name;
        const cookieKey = this.ACCESS_TOKEN_COOKIE_KEY;
        const expiresAtKey = this.EXPIRES_AT_COOKIE_KEY;
        const cookieExists: boolean = this.cookieService.check(cookieKey);

        if (cookieExists) {
            const expiresAtStr = this.cookieService.get(expiresAtKey);
            if (expiresAtStr) {
                const expiresAt = +expiresAtStr;
                if (new Date().getTime() > expiresAt) {
                    this.deleteCookies();
                    return false;
                }
            }
            return true;
        }

        return false;
    }

    deleteCookies() {
        const env = environment.name;
        const domain = location.hostname;

        this.cookieService.delete(this.ACCESS_TOKEN_COOKIE_KEY, '/', domain);
        this.cookieService.delete(this.EXPIRES_AT_COOKIE_KEY, '/', domain);

        // Also try to delete without domain for localhost
        this.cookieService.delete(this.ACCESS_TOKEN_COOKIE_KEY, '/');
        this.cookieService.delete(this.EXPIRES_AT_COOKIE_KEY, '/');
    }

    deleteStorage() {
        sessionStorage.clear();
        localStorage.clear();
    }

}